import React, { Fragment, useEffect } from 'react';
import { Row, Col, Card, Typography, Button, Tag, Flex, Divider, Spin, Alert } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import EditIcon from '../../components/icon/EditIcon';
import { getContract, archiveContract, setContract } from './redux/slice';
import { primary, secondary, muted, warning } from '../../utils/theme/config';

const { Title, Text } = Typography;

const statusColors = {
    active: primary,
    cancelled: secondary,
    expired: '#fa8c16',
    archived: muted,
};

const statusLabels = {
    active: 'Aktiv',
    cancelled: 'Gekündigt',
    expired: 'Abgelaufen',
    archived: 'Archiviert',
};

const intervalLabels = {
    monthly: 'monatlich',
    quarterly: 'quartalsweise',
    yearly: 'jährlich',
    once: 'einmalig',
};

const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '—';
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
};

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return dayjs(dateStr).format('DD.MM.YYYY');
};

const InfoRow = ({ label, value }) => (
    <Flex justify='space-between' align='flex-start' style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
        <Text type='secondary' style={{ minWidth: 160 }}>{label}</Text>
        <Text strong style={{ textAlign: 'right' }}>{value || '—'}</Text>
    </Flex>
);

const ContractDetail = ({ permission = {} }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const auth = useSelector(state => state.auth);
    const contracts = useSelector(state => state.contracts);
    const { contract, loading } = contracts;
    const organizer = auth?.decodeToken?.organizer;

    useEffect(() => {
        if (id) {
            dispatch(getContract(id));
        }
        return () => {
            dispatch(setContract(null));
        };
    }, [id]);

    const noticePeriodDeadline = (() => {
        if (!contract?.endDate || !contract?.noticePeriod) return null;
        return dayjs(contract.endDate).subtract(contract.noticePeriod, 'day');
    })();

    const isNoticeUrgent = noticePeriodDeadline
        ? noticePeriodDeadline.diff(dayjs(), 'day') <= 30
        : false;

    const getFilterData = () => ({
        search: '',
        sortBy: 'name',
        order: 'asc',
        page: 1,
        limit: 10,
        organizer,
        filters: {},
    });

    if (loading && !contract) {
        return (
            <Flex justify='center' align='center' style={{ minHeight: 300 }}>
                <Spin size='large' />
            </Flex>
        );
    }

    if (!contract) {
        return <Text type='secondary'>Vertrag nicht gefunden.</Text>;
    }

    return (
        <Fragment>
            {/* Header */}
            <Row justify='space-between' align='middle' className='bg-white padding-1 rounded-1' gutter={[16, 16]}>
                <Col>
                    <Flex align='center' gap={12}>
                        <Title level={4} type='success' style={{ margin: 0 }}>{contract.name}</Title>
                        <Tag color={statusColors[contract.status] || muted}>
                            {statusLabels[contract.status] || contract.status}
                        </Tag>
                    </Flex>
                    {contract.uniqueIdentifier && (
                        <Text type='secondary'>Nr. {contract.uniqueIdentifier}</Text>
                    )}
                </Col>
                <Col>
                    <Flex gap={8}>
                        <Button onClick={() => navigate('/contract-manager/list')}>Zurück</Button>
                        {permission?.canUpdate && (
                            <Button
                                type='primary'
                                icon={<EditIcon />}
                                onClick={() => navigate(`/contract-manager/edit/${id}`)}
                            >
                                Bearbeiten
                            </Button>
                        )}
                        <Button
                            onClick={() => navigate(`/contract-manager/ai/${id}`)}
                        >
                            KI-Auslesen
                        </Button>
                        {permission?.canDelete && (
                            <Button
                                danger
                                onClick={() => {
                                    dispatch(archiveContract({ id, filterData: getFilterData() }));
                                    navigate('/contract-manager/list');
                                }}
                            >
                                Archivieren
                            </Button>
                        )}
                    </Flex>
                </Col>
            </Row>

            {isNoticeUrgent && (
                <Alert
                    className='padding-top-1'
                    message={`Achtung: Kündigungsfrist endet am ${noticePeriodDeadline.format('DD.MM.YYYY')} (in ${noticePeriodDeadline.diff(dayjs(), 'day')} Tagen)`}
                    type='error'
                    showIcon
                    style={{ marginTop: 12 }}
                />
            )}

            <Row gutter={[16, 16]} className='padding-top-1'>
                {/* Grunddaten */}
                <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
                    <Card title={<Text strong>Grunddaten</Text>} className='border-green'>
                        <InfoRow label='Kategorie' value={contract.categoryDetails?.name || '—'} />
                        <InfoRow label='Vertragspartner' value={contract.partner} />
                        <InfoRow label='Vertragsnummer' value={contract.contractNumber} />
                        <InfoRow label='Kundennummer' value={contract.customerNumber} />
                        <InfoRow label='Ansprechpartner' value={contract.contactPerson} />
                    </Card>
                </Col>

                {/* Laufzeit */}
                <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
                    <Card title={<Text strong>Laufzeit & Kündigung</Text>} className='border-green'>
                        <InfoRow label='Startdatum' value={formatDate(contract.startDate)} />
                        <InfoRow label='Enddatum' value={formatDate(contract.endDate)} />
                        <InfoRow label='Mindestlaufzeit' value={contract.minimumTerm ? `${contract.minimumTerm} Monate` : null} />
                        <InfoRow label='Kündigungsfrist' value={contract.noticePeriod ? `${contract.noticePeriod} Tage` : null} />
                        <InfoRow label='Auto-Verlängerung' value={contract.autoRenewal ? 'Ja' : 'Nein'} />
                        {contract.autoRenewal && (
                            <InfoRow label='Verlängerungszeitraum' value={contract.renewalPeriod ? `${contract.renewalPeriod} Monate` : null} />
                        )}
                    </Card>
                </Col>

                {/* Kosten */}
                <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
                    <Card title={<Text strong>Kosten</Text>} className='border-green'>
                        <InfoRow label='Kostenbetrag' value={formatCurrency(contract.costAmount)} />
                        <InfoRow label='Kostenintervall' value={intervalLabels[contract.costInterval] || contract.costInterval} />
                        <InfoRow label='Nächste Zahlung' value={formatDate(contract.nextPaymentDate)} />
                    </Card>
                </Col>

                {/* Verwaltung */}
                <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
                    <Card title={<Text strong>Verwaltung</Text>} className='border-green'>
                        <InfoRow label='Zuständige Person' value={contract.responsiblePerson} />
                        <InfoRow label='Standort / Kostenstelle' value={contract.location} />
                        <div style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                            <Text type='secondary' style={{ minWidth: 160 }}>Tags</Text>
                            <Flex wrap gap={4} style={{ marginTop: 4 }}>
                                {(contract.tags || []).length > 0
                                    ? contract.tags.map((t) => <Tag key={t}>{t}</Tag>)
                                    : <Text>—</Text>}
                            </Flex>
                        </div>
                        {contract.notes && (
                            <div style={{ padding: '6px 0' }}>
                                <Text type='secondary'>Notizen</Text>
                                <div style={{ marginTop: 4 }}>
                                    <Text>{contract.notes}</Text>
                                </div>
                            </div>
                        )}
                    </Card>
                </Col>

                {/* Fristen */}
                <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
                    <Card title={<Text strong>Fristen</Text>} className='border-green'>
                        <InfoRow
                            label='Kündigungsdeadline'
                            value={noticePeriodDeadline
                                ? <Text style={{ color: isNoticeUrgent ? secondary : 'inherit' }}>{noticePeriodDeadline.format('DD.MM.YYYY')}</Text>
                                : '—'}
                        />
                        <InfoRow label='Enddatum' value={formatDate(contract.endDate)} />
                        {contract.autoRenewal && (
                            <InfoRow
                                label='Nächste Verlängerung'
                                value={contract.endDate ? formatDate(contract.endDate) : '—'}
                            />
                        )}
                    </Card>
                </Col>

                {/* Custom Fields */}
                {contract.customValues && contract.customValues.length > 0 && (
                    <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
                        <Card title={<Text strong>Dynamische Felder</Text>} className='border-green'>
                            {contract.customValues.map((cv) => (
                                <InfoRow key={cv.fieldKey} label={cv.fieldName} value={String(cv.value)} />
                            ))}
                        </Card>
                    </Col>
                )}

                {/* Dateien */}
                {contract.files && contract.files.length > 0 && (
                    <Col xs={24}>
                        <Card title={<Text strong>Dateien</Text>} className='border-green'>
                            <Flex vertical gap={8}>
                                {contract.files.map((file, idx) => (
                                    <a
                                        key={idx}
                                        href={`${process.env.REACT_APP_API_URI}/contracts/${file.filename || file}`}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        style={{ color: primary }}
                                    >
                                        {file.originalName || file.filename || `Datei ${idx + 1}`}
                                    </a>
                                ))}
                            </Flex>
                        </Card>
                    </Col>
                )}
            </Row>
        </Fragment>
    );
};

export default ContractDetail;
